import { DateTime } from 'luxon';
import { Clickable, ClickHandler } from 'dg-web-shared/ui/Clickable';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16';
import {
    getOrElse,
    isDefined,
    isUndefined,
    Maybe,
    thenElse,
} from 'dg-web-shared/lib/MaybeV2';
import { Formatter } from 'dg-web-shared/lib/Date';
import { range } from 'dg-web-shared/lib/ArrayUtil';
import { css } from '@emotion/css';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { Colors, rgba } from 'dg-web-shared/ui/vars';
import React, { type JSX } from 'react';
import { Icon16 } from 'dg-web-shared/ui/icons/Icon';

interface WeekdayProps {
    day: DateTime;
    language: string;
}

const Weekday = (p: WeekdayProps): JSX.Element => (
    <div
        className={css({
            ...Typo.caption,
            color: Colors.rgba(Colors.action_w, 0.6),
            float: 'left',
            width: '44px',
            height: '44px',
            textAlign: 'center',
            paddingTop: '13px',
        })}
    >
        {p.day.setLocale(p.language).toFormat('ccc').substring(0, 1)}
    </div>
);

interface WeekdaysProps {
    day: DateTime;
    language: string;
}

const Weekdays = (p: WeekdaysProps): JSX.Element => (
    <div
        className={css({
            height: '44px',
            width: '100%',
            position: 'absolute',
            top: '44px',
        })}
    >
        {range(7).map(i => (
            <Weekday
                day={p.day.plus({ days: i })}
                language={p.language}
                key={i}
            />
        ))}
    </div>
);

interface MonthYearNavigationProps {
    visibleDate: DateTime;
    onPrev: Maybe<ClickHandler>;
    onNext: Maybe<ClickHandler>;
    language: string;
}

function getIconButtonStyle(disabled: boolean, type: 'prev' | 'next'): string {
    return css([
        {
            position: 'absolute',
            width: '40px',
            height: '100%',
            padding: '12px',
        },
        disabled && {
            color: rgba(Colors.darkblue, 0.4),
        },
        !disabled && {
            '&:hover': {
                background: Colors.lightblue,
                color: Colors.white,
            },
        },
        type === 'prev' && {
            left: 0,
        },
        type === 'next' && {
            right: 0,
        },
    ]);
}

const MonthYearNavigation = (p: MonthYearNavigationProps): JSX.Element => {
    return (
        <div
            className={css({
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '40px',
                color: Colors.blue,
            })}
        >
            <Clickable
                element="div"
                className={getIconButtonStyle(isUndefined(p.onPrev), 'prev')}
                onClick={p.onPrev}
                disabled={isUndefined(p.onPrev)}
            >
                <Icon16 icon={Icons16.chevronLeft} />
            </Clickable>
            <Clickable
                element="div"
                className={getIconButtonStyle(isUndefined(p.onNext), 'next')}
                onClick={p.onNext}
                disabled={isUndefined(p.onNext)}
            >
                <Icon16 icon={Icons16.chevronRight} />
            </Clickable>
            <div
                className={css([
                    {
                        margin: '0 40px',
                        textAlign: 'center',
                        height: '100%',
                        paddingTop: '11px',
                        ...Typo.body,
                    },
                ])}
            >
                {Formatter.monthNameYear(
                    p.visibleDate,
                    Formatter.getLocaleFromString(p.language),
                )}
            </div>
        </div>
    );
};

export type DateSelectHandler = (d: DateTime) => void;

interface CalendarElementProps {
    day: DateTime;
    onSelect: DateSelectHandler;
    currentDate: DateTime;
    selectedDate: Maybe<DateTime>;
    minDate: Maybe<DateTime>;
    maxDate: Maybe<DateTime>;
}

interface DayProps extends CalendarElementProps {
    invalid: boolean;
    inactive: boolean;
}

function Day(props: DayProps) {
    const [hover, setHover] = React.useState(false);

    function getState(): string {
        if (props.invalid) {
            return 'invalid';
        } else if (props.inactive) {
            return 'inactive';
        } else {
            return 'active';
        }
    }

    const current = props.day.hasSame(props.currentDate, 'day');
    const selected = thenElse(
        props.selectedDate,
        d => props.day.hasSame(d, 'day'),
        false,
    );
    return (
        <Clickable
            element="div"
            className={css([
                {
                    ...Typo.body,
                    margin: '4px',
                    color: Colors.blue,
                    width: '36px',
                    height: '36px',
                    float: 'left',
                    textAlign: 'center',
                    borderRadius: '50%',
                    position: 'relative',
                    border: `2px solid ${Colors.rgba('#000', 0)}`,
                },
                getState() === 'active' && [
                    hover && {
                        color: Colors.white,
                        background: Colors.lightblue,
                    },
                    !selected && [
                        current && [
                            {
                                borderColor: Colors.blue,
                            },
                            hover && {
                                borderColor: Colors.lightblue,
                            },
                        ],
                    ],
                    selected && {
                        background: Colors.blue,
                        color: Colors.white,
                    },
                ],
            ])}
            disabled={props.invalid || props.inactive}
            onClick={() => props.onSelect(props.day)}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
        >
            <div
                className={css([
                    {
                        paddingTop: '4px',
                        height: '100%',
                        width: '100%',
                        border: `2px solid ${Colors.rgba('#000', 0)}`,
                        borderRadius: '50%',
                    },
                    getState() === 'inactive' && {
                        color: Colors.rgba(Colors.action_w, 0.6),
                    },
                    !props.invalid && [
                        current && [
                            hover && {
                                borderColor: Colors.white,
                            },
                        ],
                        selected && [
                            { borderColor: Colors.darkblue },
                            current && { borderColor: Colors.white },
                        ],
                    ],
                ])}
            >
                {props.invalid ? '' : props.day.toFormat('d')}
            </div>
        </Clickable>
    );
}

interface WeekProps extends CalendarElementProps {
    month: DateTime;
}

const Week = (p: WeekProps): JSX.Element => {
    const { minDate, maxDate } = p;
    return (
        <div className={css({ height: '44px', width: '100%' })}>
            {range(7).map(d => {
                const day = p.day.plus({ days: d });

                const outsideRange =
                    (isDefined(minDate) && day < minDate) ||
                    (isDefined(maxDate) && day > maxDate.endOf('day'));

                return (
                    <Day
                        key={day.toISODate()}
                        {...p}
                        day={day}
                        invalid={!day.hasSame(p.month, 'month')}
                        inactive={outsideRange}
                    />
                );
            })}
        </div>
    );
};

type MonthProps = CalendarElementProps;

const Month = (p: MonthProps): JSX.Element => (
    <div
        className={css({
            position: 'absolute',
            top: '88px',
            bottom: 0,
            width: '100%',
        })}
    >
        {range(6)
            .map(w => p.day.startOf('week').plus({ weeks: w }))
            .map(d => (
                <Week key={d.toISODate()} {...p} day={d} month={p.day} />
            ))}
    </div>
);

export interface DatePickerProps {
    currentDate: DateTime;
    selectedDate: Maybe<DateTime>;
    onSelect: DateSelectHandler;
    minDate?: Maybe<DateTime>;
    maxDate?: Maybe<DateTime>;
    language: string;
    resetVisibleDate?: Maybe<boolean>;
}

const getVisibleDate = (p: DatePickerProps): DateTime => {
    const selected = getOrElse(p.selectedDate, p.currentDate);
    const min = getOrElse(p.minDate, p.currentDate);
    return min > selected ? min : selected;
};

export function DatePicker(props: DatePickerProps) {
    const [visibleDate, setVisibleDate] = React.useState(() =>
        getVisibleDate(props),
    );

    // Was UNSAFE_componentWillReceiveProps: jump back to the relevant month
    // when the caller flips `resetVisibleDate`. React's documented equivalent
    // is to adjust the state during render when that prop has changed.
    const [previousReset, setPreviousReset] = React.useState(
        props.resetVisibleDate,
    );
    if (previousReset !== props.resetVisibleDate) {
        setPreviousReset(props.resetVisibleDate);
        setVisibleDate(getVisibleDate(props));
    }

    function getPrevMonthHandler(): Maybe<ClickHandler> {
        const { minDate } = props;
        if (isDefined(minDate) && visibleDate.startOf('month') < minDate) {
            return null;
        } else {
            return () => setVisibleDate(visibleDate.minus({ months: 1 }));
        }
    }

    function getNextMonthHandler(): Maybe<ClickHandler> {
        const { maxDate } = props;
        if (isDefined(maxDate) && visibleDate.endOf('month') > maxDate) {
            return null;
        } else {
            return () => setVisibleDate(visibleDate.plus({ months: 1 }));
        }
    }

    return (
        <div
            className={css({
                height: '352px',
                width: '308px',
                position: 'relative',
            })}
        >
            <MonthYearNavigation
                visibleDate={visibleDate}
                onPrev={getPrevMonthHandler()}
                onNext={getNextMonthHandler()}
                language={props.language}
            />
            <Weekdays
                day={DateTime.now().startOf('week')}
                language={props.language}
            />
            <Month
                currentDate={props.currentDate}
                selectedDate={props.selectedDate}
                day={visibleDate.startOf('month')}
                onSelect={props.onSelect}
                minDate={props.minDate}
                maxDate={props.maxDate}
            />
        </div>
    );
}
